import { useState } from "react";
import { Link } from "react-router-dom";
import classNames from "classnames";
import PropTypes from "prop-types";
import { getNavigationItems, hasSubmenu, getNavigationItem } from "../../constants/navigation";
import CalculatorMenuLink from "../calculators/CalculatorMenuLink";
import ResourceMenuLink from "../resources/ResourceMenuLink";
import GuidelinesMenuLink from "../Guidelines/GuidelinesMenuLink";
import GuidelineMenuLink from "../Guideline/GuidelineMenuLink";

/**
 * handles multi-level navigation
 * used by both AppSidebar and MobileHeader
 */
const AppNavigation = ({
  isExpanded,
  styles,
  variant = "desktop", // 'desktop' | 'mobile'
  onNavigate,
  onSubmenuToggle,
}) => {
  const [activeSubmenu, setActiveSubmenu] = useState(null);

  const navigationItems = getNavigationItems();

  const handleSubmenuToggle = (itemId) => {
    const newActiveSubmenu = activeSubmenu === itemId ? null : itemId;
    setActiveSubmenu(newActiveSubmenu);
    onSubmenuToggle?.(newActiveSubmenu);
  };

  const handleNavigate = (path) => {
    onNavigate?.(path);
  };

  const renderMainNavigation = () => {
    return (
      <div className={styles.mainNavContainer}>
        {variant === "mobile" && (
          <div className={styles.submenuHeader}>
            <h3 className={classNames("size-20 bold", styles.submenuTitle)}>
              <span>Main Menu</span>
            </h3>
          </div>
        )}
        {navigationItems.map((item) => {
          // Mobile: Use submenu navigation for all items with submenus
          if (variant === "mobile" && hasSubmenu(item.id)) {
            return (
              <Link
                key={item.id}
                to="#"
                className={classNames(styles.navItem, { [styles.expandedNavItem]: isExpanded })}
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmenuToggle(item.id);
                }}
                title={item.title}
              >
                <i className={classNames(styles[item.iconStyle], item.icon)}></i>
                <span className={classNames("size-16 semibold", styles.linkText)}>
                  {item.title}
                </span>
              </Link>
            );
          }

          if (item.id === "calculators" && variant === "desktop") {
            return (
              <CalculatorMenuLink
                key={item.id}
                className={classNames(styles.navItem, {
                  [styles.expandedNavItem]: isExpanded,
                })}
                title={item.title}
              >
                <i className={classNames(styles[item.iconStyle], item.icon)}></i>
                {isExpanded && (
                  <span className={classNames("size-16 semibold", styles.linkText)}>
                    {item.title}
                  </span>
                )}
              </CalculatorMenuLink>
            );
          }

          if (item.id === "guidelines" && variant === "desktop") {
            return (
              <GuidelinesMenuLink
                key={item.id}
                className={classNames(styles.navItem, {
                  [styles.expandedNavItem]: isExpanded,
                })}
                title={item.title}
              >
                <i className={classNames(styles[item.iconStyle], item.icon)}></i>
                {isExpanded && (
                  <span className={classNames("size-16 semibold", styles.linkText)}>
                    {item.title}
                  </span>
                )}
              </GuidelinesMenuLink>
            );
          }

          if (item.id === "resources" && variant === "desktop") {
            return (
              <ResourceMenuLink
                key={item.id}
                className={classNames(styles.navItem, {
                  [styles.expandedNavItem]: isExpanded,
                })}
                title={item.title}
              >
                <i className={classNames(styles[item.iconStyle], item.icon)}></i>
                {isExpanded && (
                  <span className={classNames("size-16 semibold", styles.linkText)}>
                    {item.title}
                  </span>
                )}
              </ResourceMenuLink>
            );
          }

          // For other navigation items, including Guidelines on desktop, use regular Link
          return (
            <Link
              key={item.id}
              to={item.path}
              className={classNames(styles.navItem, {
                [styles.expandedNavItem]: isExpanded,
              })}
              title={item.title}
              onClick={() => handleNavigate(item.path)}
            >
              <i className={classNames(styles[item.iconStyle], item.icon)}></i>
              {(isExpanded || variant === "mobile") && (
                <span className={classNames("size-16 semibold", styles.linkText)}>
                  {item.title}
                </span>
              )}
            </Link>
          );
        })}
      </div>
    );
  };

  const renderSubmenu = (parentItem) => {
    if (!parentItem.subMenuItems) return null;

    const handleGuidelineClick = (item) => {
      // Use window.location for direct navigation (full page reload resets all state)
      window.location.href = item.path;
      setActiveSubmenu(null);
    };

    return (
      <div
        className={classNames(styles.submenu, {
          [styles.expandedSubmenu]: isExpanded || variant === "mobile",
        })}
      >
        {/* Header with back button and title */}
        <div className={styles.submenuHeader}>
          <button
            className={classNames(styles.submenuBackButton)}
            onClick={() => setActiveSubmenu(null)}
          >
            <i className="tg-icons-RightArrow"></i>
          </button>
          <h3 className={classNames("size-20 bold", styles.submenuTitle)}>
            <i className={classNames(styles[parentItem.iconStyle], parentItem.icon)}></i>
            <span>{parentItem.title}</span>
          </h3>
          <div className={styles.spacer}></div>
        </div>

        {/* Submenu items - Different rendering for Guidelines vs others */}
        {parentItem.id === "guidelines" ? (
          // Guidelines: Render with colored icons
          <div className={styles.guidelinesSubmenuContent}>
            {parentItem.subMenuItems.map((subItem) => (
              <div key={subItem.id} className={styles.guidelineSubmenuItem}>
                <GuidelineMenuLink
                  iconClassSuffix={subItem.iconClassSuffix}
                  linkText={subItem.title}
                  showArrowIcon={true}
                  onClickAction={() => handleGuidelineClick(subItem)}
                />
              </div>
            ))}
          </div>
        ) : (
          // Calculators/Resources: Render as simple text links
          parentItem.subMenuItems.map((subItem) => {
            if (subItem.isExternal) {
              return (
                <a
                  key={subItem.id}
                  href={subItem.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.submenuItem}
                >
                  <span className={classNames("size-16", styles.submenuItemText)}>
                    {subItem.title}
                  </span>
                </a>
              );
            } else {
              return (
                <Link
                  key={subItem.id}
                  to={subItem.path}
                  className={styles.submenuItem}
                  onClick={() => {
                    handleNavigate(subItem.path);
                    setActiveSubmenu(null); // close submenu after selection
                  }}
                >
                  <span className={classNames("size-16", styles.submenuItemText)}>
                    {subItem.title}
                  </span>
                </Link>
              );
            }
          })
        )}
      </div>
    );
  };

  return (
    <nav
      className={classNames(styles.mainNav, {
        [styles.expandedMainNav]: isExpanded,
      })}
    >
      {activeSubmenu && variant === "mobile"
        ? // Mobile: Show submenu in full screen for all submenu items
          renderSubmenu(getNavigationItem(activeSubmenu))
        : // Desktop or no active submenu: Show main navigation
          renderMainNavigation()}
    </nav>
  );
};

AppNavigation.propTypes = {
  isExpanded: PropTypes.bool.isRequired,
  styles: PropTypes.object.isRequired,
  variant: PropTypes.oneOf(["desktop", "mobile"]),
  onNavigate: PropTypes.func,
  onSubmenuToggle: PropTypes.func,
};

export default AppNavigation;
